import React from 'react';
import { Shield, Clock, Award, Users, CheckCircle, ThumbsUp } from 'lucide-react';

const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Shield,
      title: 'Attorney-Backed Filing',
      description: 'Every application is reviewed by a licensed IP professional before it reaches the USPTO.',
      color: 'from-gold-400 to-gold-400'
    },
    {
      icon: Clock,
      title: 'Filed in 24 Hours',
      description: 'Submit your details today and your application is prepared and filed by tomorrow.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Award,
      title: '98% Approval Rate',
      description: 'Our thorough pre-filing process means fewer rejections and faster approvals.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Users,
      title: 'Dedicated Advisors',
      description: 'A real person assigned to your case who answers calls, not an automated system.',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: CheckCircle,
      title: '10,000+ Brands Filed',
      description: 'From solo founders to multi-brand companies, we handle filings at every scale.',
      color: 'from-gold-400 to-gold-300'
    },
    {
      icon: ThumbsUp,
      title: 'Flat-Fee Pricing',
      description: 'No hourly billing or surprise invoices. You know exactly what you pay before you start.',
      color: 'from-gold-400 to-cyan-500'
    }
  ];

  return (
    <section className="md:py-20 py-5 bg-navy-800">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 md:gap-12 gap-5 items-center">
          {/* Left Side - Image */}
          <div className="relative animate-fade-in">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl hover-zoom">
              <img
                src="https://images.unsplash.com/photo-1758518729263-e26fb50db6bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlYW0lMjBzdHJhdGVneSUyMG1lZXRpbmclMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzczOTQwMDc2fDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Legal advisors reviewing client strategy"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 to-transparent"></div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="animate-slide-up">
            <div className="inline-block bg-navy-700 px-4 py-2 rounded-full mb-4">
              <span className="text-gold-400 font-semibold">WHY CHOOSE US</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-100 mb-6 leading-tight">
              The Difference Between{' '}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                Risk and Protection
              </span>
            </h2>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              Most businesses wait until a competitor files first. We make sure that never happens. Our pre-filing process, attorney-led review, and proactive monitoring give your brand the strongest possible defense from day one.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-3 group hover:translate-x-2 transition-all duration-300"
                    style={{animationDelay: `${index * 0.1}s`}}
                  >
                    <div className={`bg-gradient-to-r ${feature.color} p-2 rounded-lg group-hover:scale-110 transition-transform animate-pulse-glow`}>
                      <Icon className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-100 mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-300">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;